// src/components/store/sections/navbar/brandingConfig.tsx
"use client";

import React, { useState, useEffect } from "react";
import Link from "next/link";
import { Store } from "lucide-react";

export interface NavbarBrandingConfig {
  brandDisplayMode?: "both" | "logo_only" | "name_only";
  showLogo?: boolean;
  showBrandText?: boolean;
  showTagline?: boolean;
  showSmileDot?: boolean;
  showLogoBox?: boolean;
  logoHeight?: number;
  logoMaxWidth?: number;
  logoScale?: number;
  brandFontSize?: number;
  offsetX?: number;
  offsetY?: number;
  paddingLeft?: number;
  paddingRight?: number;
  paddingY?: number;
  itemGap?: number;
  customColorsEnabled?: boolean;
  navbarBgColor?: string;
  topBarBgColor?: string;
  subNavBgColor?: string;
  navbarTextColor?: string;
  brandTextColor?: string;
  accentColor?: string;
}

export const DEFAULT_NAVBAR_BRANDING: Required<NavbarBrandingConfig> = {
  brandDisplayMode: "both",
  showLogo: true,
  showBrandText: true,
  showTagline: false,
  showSmileDot: true,
  showLogoBox: false,
  logoHeight: 40,
  logoMaxWidth: 180,
  logoScale: 100,
  brandFontSize: 22,
  offsetX: 0,
  offsetY: 0,
  paddingLeft: 0,
  paddingRight: 0,
  paddingY: 0,
  itemGap: 10,
  customColorsEnabled: false,
  navbarBgColor: "#131921",
  topBarBgColor: "#0f172a",
  subNavBgColor: "#232f3e",
  navbarTextColor: "#ffffff",
  brandTextColor: "#ffffff",
  accentColor: "#febd69",
};

const toNum = (val: any, fallback: number): number => {
  const n = Number(val);
  return Number.isFinite(n) ? n : fallback;
};

const toBool = (val: any, fallback: boolean): boolean => {
  if (typeof val === "boolean") return val;
  if (val === "true" || val === "1" || val === 1) return true;
  if (val === "false" || val === "0" || val === 0) return false;
  return fallback;
};

export function resolveCurrentSubdomain(explicitSubdomain?: any): string {
  let raw = "";
  if (typeof explicitSubdomain === "string") {
    raw = explicitSubdomain;
  } else if (Array.isArray(explicitSubdomain) && typeof explicitSubdomain[0] === "string") {
    raw = explicitSubdomain[0];
  } else if (explicitSubdomain && typeof explicitSubdomain === "object") {
    raw =
      typeof explicitSubdomain.subdomain === "string"
        ? explicitSubdomain.subdomain
        : typeof explicitSubdomain.tenant === "string"
        ? explicitSubdomain.tenant
        : "";
  }

  const cleaned = String(raw || "").trim().toLowerCase();
  if (cleaned && !["default", "undefined", "null", "nextlook", ""].includes(cleaned)) {
    return cleaned.split(".")[0];
  }

  if (typeof window !== "undefined") {
    const host = window.location.hostname.toLowerCase();
    const parts = host.split(".");
    if (parts.length >= 2 && !["www", "localhost", "127", "api"].includes(parts[0])) {
      return parts[0];
    }
  }

  if (cleaned && !["default", "undefined", "null", ""].includes(cleaned)) {
    return cleaned.split(".")[0];
  }

  return "default";
}

export function isGenericDefaultStoreName(name?: any): boolean {
  if (!name || typeof name !== "string") return true;
  const clean = name.trim().toLowerCase();
  return ["", "nextlook", "nextlook studio", "my store", "store"].includes(clean);
}

export function normalizeBrandingConfig(raw?: any): Required<NavbarBrandingConfig> {
  if (!raw || typeof raw !== "object") {
    return { ...DEFAULT_NAVBAR_BRANDING };
  }

  const src =
    raw.navbar_branding && typeof raw.navbar_branding === "object"
      ? { ...raw, ...raw.navbar_branding }
      : raw.branding && typeof raw.branding === "object"
      ? { ...raw, ...raw.branding }
      : raw.theme_config?.navbar_branding && typeof raw.theme_config.navbar_branding === "object"
      ? { ...raw, ...raw.theme_config.navbar_branding }
      : raw;

  const modeRaw = src.brandDisplayMode ?? src.brand_display_mode;
  const brandDisplayMode: "both" | "logo_only" | "name_only" =
    modeRaw === "logo_only" || modeRaw === "name_only" || modeRaw === "both"
      ? modeRaw
      : DEFAULT_NAVBAR_BRANDING.brandDisplayMode;

  const showLogo =
    brandDisplayMode === "name_only"
      ? false
      : toBool(src.showLogo ?? src.show_logo, DEFAULT_NAVBAR_BRANDING.showLogo);

  const showBrandText =
    brandDisplayMode === "logo_only"
      ? false
      : toBool(src.showBrandText ?? src.show_brand_text, DEFAULT_NAVBAR_BRANDING.showBrandText);

  return {
    brandDisplayMode,
    showLogo,
    showBrandText,
    showTagline: toBool(src.showTagline ?? src.show_tagline, DEFAULT_NAVBAR_BRANDING.showTagline),
    showSmileDot: toBool(src.showSmileDot ?? src.show_smile_dot, DEFAULT_NAVBAR_BRANDING.showSmileDot),
    showLogoBox: toBool(src.showLogoBox ?? src.show_logo_box, DEFAULT_NAVBAR_BRANDING.showLogoBox),
    logoHeight: toNum(src.logoHeight ?? src.logo_height, DEFAULT_NAVBAR_BRANDING.logoHeight),
    logoMaxWidth: toNum(src.logoMaxWidth ?? src.logo_max_width, DEFAULT_NAVBAR_BRANDING.logoMaxWidth),
    logoScale: toNum(src.logoScale ?? src.logo_scale, DEFAULT_NAVBAR_BRANDING.logoScale),
    brandFontSize: toNum(src.brandFontSize ?? src.brand_font_size, DEFAULT_NAVBAR_BRANDING.brandFontSize),
    offsetX: toNum(src.offsetX ?? src.offset_x, DEFAULT_NAVBAR_BRANDING.offsetX),
    offsetY: toNum(src.offsetY ?? src.offset_y, DEFAULT_NAVBAR_BRANDING.offsetY),
    paddingLeft: toNum(src.paddingLeft ?? src.padding_left, DEFAULT_NAVBAR_BRANDING.paddingLeft),
    paddingRight: toNum(src.paddingRight ?? src.padding_right, DEFAULT_NAVBAR_BRANDING.paddingRight),
    paddingY: toNum(src.paddingY ?? src.padding_y, DEFAULT_NAVBAR_BRANDING.paddingY),
    itemGap: toNum(src.itemGap ?? src.item_gap, DEFAULT_NAVBAR_BRANDING.itemGap),
    customColorsEnabled: toBool(
      src.customColorsEnabled ?? src.custom_colors_enabled,
      DEFAULT_NAVBAR_BRANDING.customColorsEnabled
    ),
    navbarBgColor:
      typeof (src.navbarBgColor ?? src.navbar_bg_color) === "string" &&
      (src.navbarBgColor ?? src.navbar_bg_color).trim()
        ? (src.navbarBgColor ?? src.navbar_bg_color).trim()
        : DEFAULT_NAVBAR_BRANDING.navbarBgColor,
    topBarBgColor:
      typeof (src.topBarBgColor ?? src.top_bar_bg_color) === "string" &&
      (src.topBarBgColor ?? src.top_bar_bg_color).trim()
        ? (src.topBarBgColor ?? src.top_bar_bg_color).trim()
        : DEFAULT_NAVBAR_BRANDING.topBarBgColor,
    subNavBgColor:
      typeof (src.subNavBgColor ?? src.sub_nav_bg_color) === "string" &&
      (src.subNavBgColor ?? src.sub_nav_bg_color).trim()
        ? (src.subNavBgColor ?? src.sub_nav_bg_color).trim()
        : DEFAULT_NAVBAR_BRANDING.subNavBgColor,
    navbarTextColor:
      typeof (src.navbarTextColor ?? src.navbar_text_color) === "string" &&
      (src.navbarTextColor ?? src.navbar_text_color).trim()
        ? (src.navbarTextColor ?? src.navbar_text_color).trim()
        : DEFAULT_NAVBAR_BRANDING.navbarTextColor,
    brandTextColor:
      typeof (src.brandTextColor ?? src.brand_text_color) === "string" &&
      (src.brandTextColor ?? src.brand_text_color).trim()
        ? (src.brandTextColor ?? src.brand_text_color).trim()
        : DEFAULT_NAVBAR_BRANDING.brandTextColor,
    accentColor:
      typeof (src.accentColor ?? src.accent_color) === "string" &&
      (src.accentColor ?? src.accent_color).trim()
        ? (src.accentColor ?? src.accent_color).trim()
        : DEFAULT_NAVBAR_BRANDING.accentColor,
  };
}

export function getStorageKey(subdomain?: any) {
  const cleanSub = resolveCurrentSubdomain(subdomain);
  return `xylo_navbar_branding_${cleanSub}`;
}

export function getCachedBrandingFromStorage(subdomain?: any): {
  branding: Required<NavbarBrandingConfig>;
  storeName?: string;
  storeTagline?: string;
  logo?: string | null;
  contactAddress?: string;
  announcementText?: string;
  announcementPhone?: string;
  showAnnouncementBar?: boolean;
  hasCache?: boolean;
} {
  if (typeof window === "undefined") {
    return { branding: { ...DEFAULT_NAVBAR_BRANDING }, hasCache: false };
  }
  try {
    const raw =
      localStorage.getItem(getStorageKey(subdomain)) ||
      localStorage.getItem("xylo_navbar_branding_default");
    if (raw) {
      const parsed = JSON.parse(raw);
      return {
        branding: normalizeBrandingConfig(parsed.branding || parsed.navbar_branding || parsed),
        storeName: parsed.store_name ?? parsed.storeName,
        storeTagline: parsed.store_tagline ?? parsed.storeTagline,
        logo: parsed.logo ?? parsed.store_logo ?? null,
        contactAddress: parsed.contact_address ?? parsed.contactAddress,
        announcementText: parsed.announcement_text ?? parsed.announcementText,
        announcementPhone: parsed.announcement_phone ?? parsed.announcementPhone,
        showAnnouncementBar: parsed.show_announcement_bar ?? parsed.showAnnouncementBar,
        hasCache: true,
      };
    }
  } catch (e) {
    console.error("Error reading cached navbar branding:", e);
  }
  return { branding: { ...DEFAULT_NAVBAR_BRANDING }, hasCache: false };
}

export function saveBrandingToStorage(
  subdomain: any,
  payload: {
    branding: Partial<NavbarBrandingConfig>;
    store_name?: string;
    store_tagline?: string;
    logo?: string | null;
    contact_address?: string;
    announcement_text?: string;
    announcement_phone?: string;
    show_announcement_bar?: boolean;
  }
) {
  if (typeof window === "undefined") return;
  try {
    const normalized = normalizeBrandingConfig(payload.branding);
    const data = JSON.stringify({
      branding: normalized,
      navbar_branding: normalized,
      store_name: payload.store_name,
      store_tagline: payload.store_tagline,
      logo: payload.logo,
      contact_address: payload.contact_address,
      announcement_text: payload.announcement_text,
      announcement_phone: payload.announcement_phone,
      show_announcement_bar: payload.show_announcement_bar,
      updatedAt: Date.now(),
    });
    localStorage.setItem(getStorageKey(subdomain), data);
    localStorage.setItem("xylo_navbar_branding_default", data);
  } catch (e) {
    console.error("Error saving navbar branding to storage:", e);
  }
}

export function useNavbarBranding(
  arg1?: any,
  arg2?: string,
  arg3?: string | null,
  arg4?: Partial<NavbarBrandingConfig>,
  arg5?: string
) {
  const isObjCall = arg1 && typeof arg1 === "object" && !Array.isArray(arg1);

  const rawSubdomain = isObjCall ? arg1.subdomain : arg1;
  const propStoreName = isObjCall ? arg1.storeName ?? arg1.store_name : arg2;
  const propLogo = isObjCall ? arg1.logo ?? arg1.logoUrl ?? arg1.store_logo : arg3;
  const propBranding = isObjCall
    ? arg1.brandingConfig ?? arg1.branding ?? arg1.navbar_branding
    : arg4;
  const propTagline = isObjCall ? arg1.storeTagline ?? arg1.tagline ?? arg1.store_tagline : arg5;
  const propPrimaryColor = isObjCall ? arg1.primaryColor ?? arg1.primary_color : "#059669";
  const propSecondaryColor = isObjCall ? arg1.secondaryColor ?? arg1.secondary_color : "#047857";
  const propContactAddress = isObjCall
    ? arg1.contactAddress ?? arg1.contact_address
    : "Khulna 9000";
  const propAnnouncementText = isObjCall
    ? arg1.announcementText ?? arg1.announcement_text
    : "Sign up and GET 20% OFF on your first order!";
  const propAnnouncementPhone = isObjCall
    ? arg1.announcementPhone ?? arg1.announcement_phone
    : "+880 1700-000000";
  const propShowAnnouncementBar = isObjCall
    ? arg1.showAnnouncementBar ?? arg1.show_announcement_bar
    : true;

  // 🌟 SSR-safe initial states (prevents React Hydration Mismatch between Server and Client)
  const [branding, setBranding] = useState<Required<NavbarBrandingConfig>>(() =>
    normalizeBrandingConfig(propBranding)
  );
  const [liveStoreName, setLiveStoreName] = useState<string>(() => propStoreName || "Store");
  const [liveTagline, setLiveTagline] = useState<string>(() => propTagline || "");
  const [liveLogo, setLiveLogo] = useState<string | null>(() => propLogo || null);
  const [liveContactAddress, setLiveContactAddress] = useState<string>(
    () => (typeof propContactAddress === "string" && propContactAddress ? propContactAddress : "Khulna 9000")
  );
  const [liveAnnouncementText, setLiveAnnouncementText] = useState<string>(
    () => propAnnouncementText || "Sign up and GET 20% OFF on your first order!"
  );
  const [liveAnnouncementPhone, setLiveAnnouncementPhone] = useState<string>(
    () => propAnnouncementPhone || "+880 1700-000000"
  );
  const [liveShowAnnouncementBar, setLiveShowAnnouncementBar] = useState<boolean>(
    () => propShowAnnouncementBar ?? true
  );

  // Sync after mount from localStorage and Backend API
  useEffect(() => {
    let isMounted = true;
    const activeSub = resolveCurrentSubdomain(rawSubdomain);
    const cached = getCachedBrandingFromStorage(activeSub);

    if (cached.hasCache) {
      setBranding(
        normalizeBrandingConfig({
          ...cached.branding,
          ...(propBranding || {}),
        })
      );
      if (cached.storeName && cached.storeName.trim()) {
        setLiveStoreName(cached.storeName);
      }
      if (cached.storeTagline !== undefined) {
        setLiveTagline(cached.storeTagline);
      }
      if (cached.logo && cached.logo !== "/logo.png") {
        setLiveLogo(cached.logo);
      }
      if (typeof cached.contactAddress === "string" && cached.contactAddress.trim()) {
        setLiveContactAddress(cached.contactAddress);
      }
      if (typeof cached.announcementText === "string" && cached.announcementText.trim()) {
        setLiveAnnouncementText(cached.announcementText);
      }
      if (typeof cached.announcementPhone === "string" && cached.announcementPhone.trim()) {
        setLiveAnnouncementPhone(cached.announcementPhone);
      }
      if (typeof cached.showAnnouncementBar === "boolean") {
        setLiveShowAnnouncementBar(cached.showAnnouncementBar);
      }
    } else if (propBranding) {
      setBranding(normalizeBrandingConfig(propBranding));
    }

    const fetchLiveBranding = async () => {
      if (typeof window === "undefined") return;
      try {
        const host = window.location.hostname;
        const isLocal = host.includes("localhost") || host === "127.0.0.1";
        const base = isLocal ? `http://${activeSub}.localhost:8000` : "";
        const res = await fetch(
          `${base}/api/v1/stores/settings/?subdomain=${encodeURIComponent(activeSub)}`,
          {
            headers: {
              Accept: "application/json",
              "X-Tenant-Subdomain": activeSub,
            },
          }
        );
        if (!res.ok || !isMounted) return;
        const data = await res.json();

        const apiBrandingRaw =
          data.navbar_branding && Object.keys(data.navbar_branding).length > 0
            ? data.navbar_branding
            : data.theme_config?.navbar_branding &&
              Object.keys(data.theme_config.navbar_branding).length > 0
            ? data.theme_config.navbar_branding
            : null;

        const latestCached = getCachedBrandingFromStorage(activeSub);
        const mergedBranding = normalizeBrandingConfig({
          ...latestCached.branding,
          ...(apiBrandingRaw || {}),
          ...(propBranding || {}),
        });

        const rawApiName = typeof data.store_name === "string" ? data.store_name.trim() : "";
        const apiStoreName =
          rawApiName && !isGenericDefaultStoreName(rawApiName)
            ? rawApiName
            : latestCached.storeName || rawApiName || propStoreName || activeSub;

        const apiTagline =
          typeof data.store_tagline === "string"
            ? data.store_tagline
            : latestCached.storeTagline || "";

        let apiLogo: string | null = latestCached.logo || null;
        const rawLogo = data.logo || data.store_logo;
        if (typeof rawLogo === "string" && rawLogo.trim()) {
          apiLogo = rawLogo;
        } else if (rawLogo && typeof rawLogo === "object" && typeof rawLogo.url === "string") {
          apiLogo = rawLogo.url;
        }

        const apiAddr =
          typeof data.contact_address === "string"
            ? data.contact_address
            : data.contact_address?.address ||
              data.contact_address?.street ||
              latestCached.contactAddress ||
              "Khulna 9000";

        if (isMounted) {
          setBranding(mergedBranding);
          if (apiStoreName) setLiveStoreName(apiStoreName);
          setLiveTagline(apiTagline);
          if (apiLogo) setLiveLogo(apiLogo);
          if (apiAddr) setLiveContactAddress(apiAddr);
          if (typeof data.announcement_text === "string") {
            setLiveAnnouncementText(data.announcement_text);
          }
          if (typeof data.announcement_phone === "string") {
            setLiveAnnouncementPhone(data.announcement_phone);
          }
          if (typeof data.show_announcement_bar === "boolean") {
            setLiveShowAnnouncementBar(data.show_announcement_bar);
          }

          saveBrandingToStorage(activeSub, {
            branding: mergedBranding,
            store_name: apiStoreName,
            store_tagline: apiTagline,
            logo: apiLogo,
            contact_address: apiAddr,
            announcement_text: data.announcement_text,
            announcement_phone: data.announcement_phone,
            show_announcement_bar: data.show_announcement_bar,
          });
        }
      } catch {
        // Fallback to cached state
      }
    };

    fetchLiveBranding();
    return () => {
      isMounted = false;
    };
  }, [rawSubdomain]);

  // Listen to real-time updates from CompanySettingsTab
  useEffect(() => {
    const activeSub = resolveCurrentSubdomain(rawSubdomain);
    const handleUpdate = (e: Event) => {
      const customEvent = e as CustomEvent;
      const d = customEvent.detail || {};
      if (d.navbar_branding || d.branding) {
        setBranding(normalizeBrandingConfig(d.navbar_branding || d.branding));
      } else {
        const cached = getCachedBrandingFromStorage(activeSub);
        setBranding(cached.branding);
      }
      if (typeof d.store_name === "string" && d.store_name.trim()) {
        setLiveStoreName(d.store_name);
      }
      if (typeof d.store_tagline === "string") {
        setLiveTagline(d.store_tagline);
      }
      if (d.logo !== undefined) {
        setLiveLogo(d.logo);
      }
      if (typeof d.contact_address === "string" && d.contact_address.trim()) {
        setLiveContactAddress(d.contact_address);
      }
      if (typeof d.announcement_text === "string") {
        setLiveAnnouncementText(d.announcement_text);
      }
      if (typeof d.announcement_phone === "string") {
        setLiveAnnouncementPhone(d.announcement_phone);
      }
      if (typeof d.show_announcement_bar === "boolean") {
        setLiveShowAnnouncementBar(d.show_announcement_bar);
      }
    };

    window.addEventListener("storeSettingsUpdated", handleUpdate);
    window.addEventListener("navbarBrandingUpdated", handleUpdate);
    window.addEventListener("storage", handleUpdate);
    return () => {
      window.removeEventListener("storeSettingsUpdated", handleUpdate);
      window.removeEventListener("navbarBrandingUpdated", handleUpdate);
      window.removeEventListener("storage", handleUpdate);
    };
  }, [rawSubdomain]);

  const isCustomColor = Boolean(branding.customColorsEnabled);

  const effectivePrimaryColor =
    isCustomColor && branding.accentColor
      ? branding.accentColor
      : propPrimaryColor || "#059669";

  const effectiveSecondaryColor =
    isCustomColor && branding.subNavBgColor
      ? branding.subNavBgColor
      : propSecondaryColor || "#047857";

  const topBarStyle: React.CSSProperties | undefined = isCustomColor
    ? {
        backgroundColor: branding.topBarBgColor,
        color: branding.navbarTextColor,
      }
    : undefined;

  const mainBarStyle: React.CSSProperties | undefined = isCustomColor
    ? {
        backgroundColor: branding.navbarBgColor,
        color: branding.navbarTextColor,
      }
    : undefined;

  const subBarStyle: React.CSSProperties | undefined = isCustomColor
    ? {
        backgroundColor: branding.subNavBgColor,
        color: branding.navbarTextColor,
      }
    : undefined;

  return {
    branding,
    liveStoreName,
    effectiveStoreName: liveStoreName,
    storeName: liveStoreName,
    liveTagline,
    effectiveTagline: liveTagline,
    storeTagline: liveTagline,
    liveLogo,
    effectiveLogo: liveLogo,
    logo: liveLogo,
    effectivePrimaryColor,
    effectiveSecondaryColor,
    effectiveContactAddress: liveContactAddress || "Khulna 9000",
    effectiveAnnouncementText:
      liveAnnouncementText || "Sign up and GET 20% OFF on your first order!",
    effectiveAnnouncementPhone: liveAnnouncementPhone || "+880 1700-000000",
    effectiveShowAnnouncementBar: liveShowAnnouncementBar ?? true,
    isCustomColor,
    topBarStyle,
    mainBarStyle,
    subBarStyle,
    customColors: {
      enabled: isCustomColor,
      navbarBg: branding.navbarBgColor,
      topBarBg: branding.topBarBgColor,
      subNavBg: branding.subNavBgColor,
      text: branding.navbarTextColor,
      brandText: branding.brandTextColor,
      accent: branding.accentColor,
    },
  };
}

interface NavbarBrandIdentityProps {
  href?: string;
  storeName?: string;
  tagline?: string;
  storeTagline?: string;
  logoUrl?: string | null;
  logo?: string | null;
  primaryColor?: string;
  secondaryColor?: string;
  accentColor?: string;
  branding?: Partial<NavbarBrandingConfig>;
  defaultTextColor?: string;
  textColor?: string;
  defaultSubtextColor?: string;
  subtextColor?: string;
  isDarkTheme?: boolean;
  variantStyle?: string;
  badgeIcon?: React.ReactNode;
  fallbackIcon?: React.ReactNode;
  className?: string;
  isPreview?: boolean;
}

export function NavbarBrandIdentity({
  href = "/",
  storeName = "Store",
  tagline,
  storeTagline,
  logoUrl,
  logo,
  primaryColor = "#059669",
  accentColor,
  branding: rawBranding,
  defaultTextColor,
  textColor,
  defaultSubtextColor = "#94a3b8",
  subtextColor,
  isDarkTheme = false,
  badgeIcon,
  fallbackIcon,
  className = "",
  isPreview = false,
}: NavbarBrandIdentityProps) {
  const b = normalizeBrandingConfig(rawBranding);

  const resolvedLogo = logoUrl ?? logo ?? null;
  const resolvedTagline = tagline ?? storeTagline ?? "";
  const cleanName = typeof storeName === "string" ? storeName.trim() : "";
  const isDotOnly = cleanName === "." || cleanName === "";

  const shouldShowLogo = b.brandDisplayMode !== "name_only" && b.showLogo;
  const shouldShowText =
    b.brandDisplayMode !== "logo_only" && b.showBrandText && !isDotOnly && cleanName.length > 0;
  const shouldShowSmile = shouldShowText && b.showSmileDot;
  const shouldShowTagline = shouldShowText && b.showTagline && Boolean(resolvedTagline.trim());

  const fallbackTextColor =
    textColor || defaultTextColor || (isDarkTheme ? "#ffffff" : "inherit");

  const resolvedTextColor = b.customColorsEnabled
    ? b.brandTextColor || b.navbarTextColor
    : fallbackTextColor;

  const resolvedAccent = b.customColorsEnabled
    ? b.accentColor || accentColor || primaryColor
    : accentColor || primaryColor;

  const scaleFactor = Math.max(0.5, Math.min(2.5, (b.logoScale || 100) / 100));

  const wrapperStyle: React.CSSProperties = {
    transform: `translate(${b.offsetX}px, ${b.offsetY}px)`,
    paddingLeft: `${b.paddingLeft}px`,
    paddingRight: `${b.paddingRight}px`,
    paddingTop: `${b.paddingY}px`,
    paddingBottom: `${b.paddingY}px`,
    gap: `${b.itemGap}px`,
  };

  const content = (
    <div
      style={wrapperStyle}
      className={`inline-flex items-center shrink-0 select-none transition-transform duration-150 ${className}`}
    >
      {shouldShowLogo &&
        (resolvedLogo && resolvedLogo !== "/logo.png" ? (
          <div
            className={`relative flex items-center justify-center shrink-0 ${
              b.showLogoBox
                ? "p-1.5 rounded-xl bg-white/10 border border-white/15 shadow-sm backdrop-blur-xs"
                : ""
            }`}
          >
            <img
              src={resolvedLogo}
              alt={cleanName || "Store Logo"}
              style={{
                height: `${b.logoHeight}px`,
                maxWidth: `${b.logoMaxWidth}px`,
                transform: scaleFactor !== 1 ? `scale(${scaleFactor})` : undefined,
                transformOrigin: "left center",
              }}
              className="w-auto object-contain block transition-all duration-150"
            />
          </div>
        ) : (
          <div
            style={{
              width: `${Math.max(28, b.logoHeight)}px`,
              height: `${Math.max(28, b.logoHeight)}px`,
              backgroundColor: resolvedAccent,
              transform: scaleFactor !== 1 ? `scale(${scaleFactor})` : undefined,
            }}
            className="rounded-xl flex items-center justify-center text-white font-black shadow-md shrink-0"
          >
            {badgeIcon || fallbackIcon || <Store className="w-5 h-5" />}
          </div>
        ))}

      {shouldShowText && (
        <div
          className="flex flex-col justify-center leading-none"
          style={{
            marginLeft:
              shouldShowLogo && resolvedLogo && scaleFactor > 1
                ? `${Math.round((scaleFactor - 1) * 28)}px`
                : undefined,
          }}
        >
          <span
            style={{
              fontSize: `${b.brandFontSize}px`,
              color: resolvedTextColor,
            }}
            className="font-black tracking-tight leading-none whitespace-nowrap"
          >
            {cleanName}
            {shouldShowSmile && <span style={{ color: resolvedAccent }}>.</span>}
          </span>

          {shouldShowTagline && (
            <span
              style={{ color: subtextColor || defaultSubtextColor }}
              className="text-[10px] font-bold tracking-wider uppercase mt-1 truncate max-w-[200px]"
            >
              {resolvedTagline}
            </span>
          )}
        </div>
      )}
    </div>
  );

  if (isPreview) {
    return <div className="cursor-pointer inline-flex items-center">{content}</div>;
  }

  return (
    <Link href={href} className="inline-flex items-center focus:outline-none group">
      {content}
    </Link>
  );
}